import { Service, inject, signal } from '@angular/core';
import { HttpClient, httpResource } from '@angular/common/http';
import {
  ChatDetail,
  ChatListItem,
  ChatListRequestQueryParams,
  FeedbackData,
  NewChatResponse,
  QueryResponseSseChunk,
} from './types';
import { urlBuilder } from '../../api-helpers';
import { firstValueFrom, Observable } from 'rxjs';
import { requestScopes } from '../../config/auth.config';
import { MsalService } from '@azure/msal-angular';

@Service()
export class ChatService {
  private http = inject(HttpClient);
  private msalService = inject(MsalService);

  private listChatParams = signal<ChatListRequestQueryParams>(undefined);
  listChats = httpResource<ChatListItem[]>(() => {
    return {
      url: urlBuilder('/chat/'),
      params: { ...this.listChatParams() },
    };
  });
  public listChatWithParams(params: ChatListRequestQueryParams): void {
    this.listChatParams.set(params);
  }

  createNewChat = () => {
    return this.http.post<NewChatResponse>(urlBuilder('/chat/'), null);
  };

  getChatDetail = (chatId: () => string) => {
    return httpResource<ChatDetail>(() => urlBuilder(`/chat/${chatId()}`));
  };

  sendMessage = (chatId: string, userQuery: string) => {
    // TODO: verify implementation of this SSE handler
    return new Observable<QueryResponseSseChunk>((subscriber) => {
      // AbortController allows RxJS unsubscriptions to cancel the network request
      const abortController = new AbortController();

      // Execute the async stream reading process
      (async () => {
        try {
          const authResult = await firstValueFrom(
            this.msalService.acquireTokenSilent({
              scopes: requestScopes,
            }),
          );

          if (!authResult?.accessToken) {
            throw new Error('Could not obtain access token from MSAL');
          }

          const body = JSON.stringify({ content: userQuery });
          const fetchOptions: RequestInit = {
            signal: abortController.signal,
            method: 'PUT',
            body,
            headers: {
              Authorization: `Bearer ${authResult.accessToken}`,
              Accept: 'text/event-stream',
              ['content-type']: 'application/json',
            },
          };

          const response = await fetch(urlBuilder(`/chat/${chatId}`), fetchOptions);

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          if (!response.body) {
            throw new Error('Response body is not readable.');
          }

          // Pipe chunks through TextDecoderStream to easily handle string fragments
          const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();

          // Keep track of incomplete text fragments across chunks
          let buffer = '';

          while (true) {
            const { value, done } = await reader.read();

            if (done) {
              this.listChats.reload();
              subscriber.complete();
              break;
            }

            buffer += value;
            // SSE events are separated by a double newline
            const parts = buffer.split('\n\n');

            // Keep the last potentially incomplete chunk in the buffer
            buffer = parts.pop() || '';

            for (const part of parts) {
              const line = part.trim();
              if (!line) continue;

              if (line.startsWith('data: ')) {
                const rawData = line.replace('data: ', '').trim();

                // Handle your custom stream termination string
                if (rawData === '[DONE]') {
                  this.listChats.reload();
                  subscriber.complete();
                  return;
                }

                // NOTE: Could throw if the data is not valid JSON for some reason
                const parsed = JSON.parse(rawData);
                subscriber.next(parsed);
              }
            }
          }
        } catch (error) {
          // Forward any network or parsing errors to the RxJS error channel
          subscriber.error(error);
        }
      })();

      // Teardown logic: triggered when user unsubscribes
      return () => {
        abortController.abort();
      };
    });
  };

  sendFeedback = (messageId: string, feedbackData: FeedbackData) => {
    return this.http.post(urlBuilder(`/chat/${messageId}/feedback`), feedbackData);
  };
}
